import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Users,
  User,
  Video,
  FileText,
  CheckCircle,
  Play,
  Download,
  MessageCircle,
  Sparkles,
  Leaf,
  Brain,
  Wind,
  Apple,
  Heart,
  ArrowRight,
  Mail,
  BookOpen,
} from "lucide-react";
import SoleaChat from "../components/SoleaChat";

const BASE_URL = window.location.origin;

// Helper per costruire URL completo per le immagini
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url}`;
};

const API = `${window.location.origin}/api`;

// ==================== HOMEPAGE BENESSERE PER POTENZIALI CLIENTI ====================
const BenessereHomePage = () => {
  const navigate = useNavigate();
  
  // Immagini per la homepage benessere (modificabili)
  const heroImages = [
    {
      url: "https://images.unsplash.com/photo-1655665436878-05be6ea2c91e?w=800",
      alt: "Persona in meditazione all'alba"
    },
    {
      url: "https://images.unsplash.com/photo-1597189186830-4d2691861220?w=800",
      alt: "Meditazione sulla spiaggia"
    },
    {
      url: "https://images.unsplash.com/photo-1604078893234-ff3a1a5d5292?w=800",
      alt: "Yoga e respirazione"
    }
  ];

  const categories = [
    {
      icon: Brain,
      title: "Meditazione",
      description: "Tecniche per la calma mentale e la consapevolezza",
      color: "from-purple-500 to-indigo-600",
      bgColor: "bg-purple-50"
    },
    {
      icon: Wind,
      title: "Respirazione",
      description: "Esercizi per ridurre stress e aumentare l'energia",
      color: "from-cyan-500 to-blue-600",
      bgColor: "bg-cyan-50"
    },
    {
      icon: Apple,
      title: "Alimentazione",
      description: "Nutrizione consapevole per il benessere",
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-50"
    },
    {
      icon: Heart,
      title: "Gestione Stress",
      description: "Strategie naturali per l'equilibrio quotidiano",
      color: "from-rose-500 to-pink-600",
      bgColor: "bg-rose-50"
    }
  ];

  return (
    <PageLayout>
      <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-cyan-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={heroImages[0].url}
              alt={heroImages[0].alt}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-teal-900/30 via-transparent to-white"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                Accesso Gratuito alla Scienza del Benessere
              </div>
              
              {/* Title */}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Benvenuto nella<br />
                <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                  Scienza del Benessere
                </span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Chiedi all'AI tutto su <strong>Meditazione</strong>, <strong>Respirazione</strong>, 
                <strong> Alimentazione</strong> e <strong>Gestione dello Stress</strong>. 
                Riceverai una ricerca scientifica personalizzata direttamente via email.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/benessere">
                  <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-white px-8 py-6 text-lg">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Chiedi all'AI del Benessere
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/benessere/articoli">
                  <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg border-2">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Esplora gli Articoli
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-slate-900 mb-4">
              Esplora le Aree del Benessere
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Scegli un'area e fai le tue domande all'intelligenza artificiale. 
              Riceverai risposte basate su ricerche scientifiche.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, index) => {
              const IconComponent = cat.icon;
              return (
                <Link key={index} to="/benessere/tecniche">
                  <Card className={`${cat.bgColor} border-0 hover:shadow-xl transition-all duration-300 cursor-pointer group h-full`}>
                    <CardContent className="p-6 text-center">
                      <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg text-slate-800 mb-2">{cat.title}</h3>
                      <p className="text-sm text-slate-600">{cat.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left - Text */}
              <div className="text-white">
                <Badge className="bg-teal-500/20 text-teal-300 border border-teal-500/30 mb-4">
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                  Servizio Gratuito
                </Badge>
                <h2 className="font-serif text-3xl font-bold mb-4">
                  Fai una Domanda, Ricevi una Ricerca Scientifica
                </h2>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  Il nostro sistema di intelligenza artificiale analizza la tua domanda e crea 
                  un articolo personalizzato basato su ricerche scientifiche. Riceverai il 
                  risultato direttamente nella tua email, completamente gratis.
                </p>
                
                <div className="space-y-3">
                  {[
                    "Risposte basate su studi scientifici",
                    "Articoli personalizzati via email",
                    "100% gratuito, senza limiti",
                    "Privacy garantita"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-400" />
                      <span className="text-slate-200">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/benessere" className="inline-block mt-8">
                  <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Inizia Ora - È Gratis
                  </Button>
                </Link>
              </div>

              {/* Right - Image */}
              <div className="relative">
                <img 
                  src={heroImages[1].url}
                  alt="Meditazione e benessere"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">AI Benessere</p>
                      <p className="text-sm text-slate-500">Sempre disponibile</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA for Professionals */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8 sm:p-12 text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-100 text-rose-700 text-sm font-medium mb-4">
              <Heart className="w-4 h-4 mr-2" />
              Per le Tue Clienti
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              Invita le Tue Clienti Fragili
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-6">
              Se hai clienti con pelle sensibile o che stanno affrontando trattamenti delicati, 
              invitale a interagire con l'AI della Scienza del Benessere. Potranno trovare 
              supporto e informazioni utili per il loro percorso.
            </p>
            <Link to="/benessere">
              <Button size="lg" className="rounded-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white">
                Scopri di Più
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-slate-100 py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Leaf className="w-5 h-5 text-teal-600" />
              <span className="font-serif font-semibold text-slate-800">La Scienza del Benessere</span>
            </div>
            <p className="text-sm text-slate-500">
              Contenuti informativi. Non sostituiscono il parere medico.
            </p>
          </div>
        </footer>
      </div>
    </PageLayout>
  );
};

// ==================== MAIN SESSIONS PAGE ====================
const SessionsPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [booking, setBooking] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [soleaChatOpen, setSoleaChatOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const videoRef = useRef(null);

  // Check user role on mount
  useEffect(() => {
    const checkUserRole = () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUserRole(payload.role);
        }
      } catch (e) {
        console.error("Error checking user role:", e);
      }
    };
    checkUserRole();
  }, []);

  // Fetch sessions and bookings (must be called before conditional return)
  useEffect(() => {
    if (userRole !== "potenziale_cliente") {
      fetchSessions();
      fetchMyBookings();
    }
  }, [userRole]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      setSessions(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle sessioni");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my`);
      setMyBookings(response.data.filter(b => b.status === "confirmed"));
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  // Handle video end - open chat automatically
  const handleVideoEnded = () => {
    setVideoPlaying(false);
    setVideoWatched(true);
    setTimeout(() => {
      setSoleaChatOpen(true);
    }, 500);
  };

  // Handle video play
  const handleVideoPlay = () => {
    setVideoPlaying(true);
  };

  // If user is potenziale_cliente, show Benessere homepage
  if (userRole === "potenziale_cliente") {
    return <BenessereHomePage />;
  }

  const isAlreadyBooked = (sessionId) => {
    return myBookings.some(b => b.course_id === sessionId);
  };

  // Verifica se la sessione è passata
  const isSessionPast = (session) => {
    const sessionDate = new Date(session.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sessionDate < today;
  };

  // Verifica se la sessione è oggi
  const isSessionToday = (session) => {
    const sessionDate = new Date(session.date);
    const today = new Date();
    return sessionDate.toDateString() === today.toDateString();
  };

  const handleBooking = async () => {
    if (!privacyAccepted || !selectedSession) {
      toast.error("È necessario accettare la privacy policy");
      return;
    }

    // Blocca iscrizione a sessioni passate
    if (isSessionPast(selectedSession)) {
      toast.error("Non è possibile iscriversi a sessioni già concluse");
      return;
    }

    setBooking(true);
    try {
      await axios.post(`${API}/sessions/${selectedSession.session_id}/book`, {
        course_id: selectedSession.session_id,
        privacy_accepted: true,
      });
      toast.success("Iscrizione completata! Riceverai un promemoria 3 giorni prima.");
      setBookingDialogOpen(false);
      setPrivacyAccepted(false);
      fetchSessions();
      fetchMyBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'iscrizione");
    } finally {
      setBooking(false);
    }
  };

  const openBookingDialog = (session) => {
    setSelectedSession(session);
    setPrivacyAccepted(false);
    setBookingDialogOpen(true);
  };

  const openDetailDialog = (session) => {
    setSelectedSession(session);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const spotsLeft = (session) => session.max_participants - session.current_participants;
  const isFull = (session) => spotsLeft(session) <= 0;

  return (
    <PageLayout>
      {/* Scrolling Announcement Banner */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 py-3 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          <span className="text-white font-semibold text-base sm:text-lg mx-8 inline-flex items-center gap-2">
            📢 Versione Beta ODR-V03 — piattaforma in continuo sviluppo, contenuti e funzionalità in progressivo aggiornamento.
          </span>
          <span className="text-emerald-200 font-semibold text-base sm:text-lg mx-8 inline-flex items-center gap-2">
            🎓 Formazione Professionale in Estetica Oncologica - ODR Academy 🎓
          </span>
          <span className="text-white font-semibold text-base sm:text-lg mx-8 inline-flex items-center gap-2">
            📢 Versione Beta ODR-V03 — piattaforma in continuo sviluppo, contenuti e funzionalità in progressivo aggiornamento.
          </span>
          <span className="text-emerald-200 font-semibold text-base sm:text-lg mx-8 inline-flex items-center gap-2">
            🎓 Formazione Professionale in Estetica Oncologica - ODR Academy 🎓
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#FDF6F7] via-white to-[#F8E8EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center min-h-[400px] sm:min-h-[500px] py-8 sm:py-12">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left space-y-4 sm:space-y-6 order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-wellness-31/artifacts/42tsb1mv_PdfImage.png"
                alt="ODR Accademia"
                className="h-16 sm:h-20 mx-auto lg:mx-0"
              />
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#8B5A6B] leading-tight">
                La Bellezza<br />
                <span className="text-[#C49A6C]">che Cura</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-[#6B5B5B] max-w-lg mx-auto lg:mx-0">
                Formazione specializzata in estetica oncologica. 
                Impara a prenderti cura della bellezza di chi affronta un percorso oncologico.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="rounded-full bg-[#B87A8A] hover:bg-[#A06878] text-white px-6 sm:px-8 btn-shine text-sm sm:text-base"
                  onClick={() => document.getElementById('sessions-list').scrollIntoView({ behavior: 'smooth' })}
                >
                  Scopri le Sessioni
                </Button>
              </div>
            </div>
            
            {/* Right: Video SOLEA */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative flex flex-col items-center">
                <div className="absolute inset-0 bg-gradient-to-br from-[#B87A8A]/20 to-[#C49A6C]/20 rounded-3xl blur-3xl scale-110"></div>
                
                {/* Video Container */}
                <div className="relative z-10 w-full max-w-[280px] sm:max-w-[320px] rounded-3xl shadow-2xl overflow-hidden bg-black">
                  <video 
                    ref={videoRef}
                    src="https://customer-assets.emergentagent.com/job_95291c56-65e7-4816-9dfa-018c5145d579/artifacts/qbitaxve_Avatar%201.mp4"
                    className="w-full h-auto object-contain"
                    poster="https://customer-assets.emergentagent.com/job_beauty-wellness-31/artifacts/90eahbfj_Gemini_Generated_Image_52lm9d52lm9d52lm.png"
                    playsInline
                    onPlay={handleVideoPlay}
                    onPause={() => setVideoPlaying(false)}
                    onEnded={handleVideoEnded}
                  />
                  
                  {/* Play Button Overlay */}
                  {!videoPlaying && (
                    <button
                      onClick={() => videoRef.current?.play()}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors group"
                    >
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/90 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 sm:w-10 sm:h-10 text-[#B87A8A] ml-1" fill="currentColor" />
                      </div>
                    </button>
                  )}
                  
                  {/* Video Controls when playing */}
                  {videoPlaying && (
                    <button
                      onClick={() => videoRef.current?.pause()}
                      className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <div className="w-3 h-3 flex gap-1">
                        <div className="w-1 h-full bg-[#B87A8A] rounded-sm"></div>
                        <div className="w-1 h-full bg-[#B87A8A] rounded-sm"></div>
                      </div>
                    </button>
                  )}
                </div>
                
                {/* Chiedi a Solea Button */}
                <Button
                  onClick={() => setSoleaChatOpen(true)}
                  className="mt-4 sm:mt-6 rounded-full bg-gradient-to-r from-[#B87A8A] to-[#9B6A7A] hover:from-[#A06878] hover:to-[#8B5A6A] text-white px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all btn-shine"
                  size="lg"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  <span className="font-medium">Chiedi a Solea</span>
                </Button>
                <p className="text-xs text-[#8B5A6B]/70 mt-2">La tua guida digitale ODR</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements - Hidden on mobile */}
        <div className="hidden sm:block absolute top-20 left-10 w-20 h-20 bg-[#B87A8A]/10 rounded-full blur-xl"></div>
        <div className="hidden sm:block absolute bottom-20 right-10 w-32 h-32 bg-[#C49A6C]/10 rounded-full blur-xl"></div>
      </div>

      {/* Sessions List */}
      <div id="sessions-list" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-2 sm:mb-3">
            Prossime Sessioni
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Iscriviti alle sessioni formative con i nostri docenti esperti.
            Riceverai un promemoria 3 giorni prima.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : sessions.length > 0 ? (
          <div className="space-y-4 sm:space-y-6">
            {sessions.map((session) => {
              const alreadyBooked = isAlreadyBooked(session.session_id);
              const full = isFull(session);
              const spots = spotsLeft(session);

              return (
                <Card 
                  key={session.session_id} 
                  className="rounded-2xl border-border/50 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`session-card-${session.session_id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Left: Date/Time */}
                      <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-4 sm:p-6 lg:p-8 flex flex-row lg:flex-col items-center justify-center gap-4 lg:gap-0 lg:min-w-[180px]">
                        <div className="flex flex-col items-center">
                          <span className="text-3xl sm:text-4xl font-bold text-primary">
                            {new Date(session.date).getDate()}
                          </span>
                          <span className="text-base sm:text-lg text-primary/80 uppercase font-medium">
                            {new Date(session.date).toLocaleDateString("it-IT", { month: "short" })}
                          </span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                          <div className="flex items-center gap-2 text-primary">
                            <Clock className="w-4 h-4" />
                            <span className="font-medium">{session.time}</span>
                          </div>
                          <Badge variant="secondary" className="rounded-full text-xs">
                            {session.duration_hours} {session.duration_hours === 1 ? "ora" : "ore"}
                          </Badge>
                        </div>
                      </div>

                      {/* Middle: Content */}
                      <div className="flex-1 p-4 sm:p-6 lg:p-8">
                        <h2 className="font-serif text-lg sm:text-xl lg:text-2xl font-semibold mb-2">
                          {session.title}
                        </h2>
                        
                        <p className="text-muted-foreground mb-4 line-clamp-2 text-sm sm:text-base">
                          {session.description}
                        </p>

                        {/* Docente Info */}
                        <div className="flex items-center gap-3 mb-4 p-2 sm:p-3 bg-secondary/50 rounded-xl">
                          {session.docente_photo ? (
                            <img 
                              src={getFullUrl(session.docente_photo)} 
                              alt={session.docente_name}
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm sm:text-base">{session.docente_name}</p>
                            {session.docente_specialization && (
                              <p className="text-xs sm:text-sm text-muted-foreground">{session.docente_specialization}</p>
                            )}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className={full ? "text-destructive font-medium" : ""}>
                              {full ? "Completo" : `${spots} posti`}
                            </span>
                          </span>
                          {session.video_url && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">Video disponibile</span>
                              <span className="sm:hidden">Video</span>
                            </span>
                          )}
                          {session.materials.length > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                              {session.materials.length} <span className="hidden sm:inline">materiali</span>
                            </span>
                          )}
                        </div>

                        {session.notes && (
                          <p className="mt-3 text-xs sm:text-sm text-muted-foreground italic line-clamp-2">
                            📝 {session.notes}
                          </p>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="p-4 sm:p-6 lg:p-8 flex flex-row lg:flex-col justify-center items-center gap-3 border-t lg:border-t-0 lg:border-l border-border/50 lg:min-w-[200px]">
                        {isSessionPast(session) ? (
                          // SESSIONE PASSATA
                          <div className="text-center p-3 sm:p-4 bg-gray-100 rounded-xl w-full">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 rounded-full bg-gray-300 flex items-center justify-center">
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                            </div>
                            <p className="font-medium text-gray-600 text-sm sm:text-base">Sessione Chiusa</p>
                            <p className="text-xs text-gray-500 mt-1">Corso già svolto</p>
                          </div>
                        ) : alreadyBooked ? (
                          <div className="text-center p-3 sm:p-4 bg-green-50 rounded-xl w-full">
                            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-1 sm:mb-2" />
                            <p className="font-medium text-green-800 text-sm sm:text-base">Sei iscritta!</p>
                            <p className="text-xs text-green-600 mt-1 hidden sm:block">Riceverai un promemoria</p>
                          </div>
                        ) : (
                          <>
                            <Button
                              className="rounded-full btn-shine flex-1 lg:flex-none lg:w-full text-sm sm:text-base"
                              disabled={full}
                              onClick={() => openBookingDialog(session)}
                              data-testid={`book-session-${session.session_id}`}
                            >
                              {full ? "Completo" : "Iscriviti"}
                            </Button>
                            <Button
                              variant="outline"
                              className="rounded-full flex-1 lg:flex-none lg:w-full text-sm sm:text-base"
                              onClick={() => openDetailDialog(session)}
                            >
                              Dettagli
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="font-serif text-xl font-medium mb-2">Nessuna sessione in programma</h3>
              <p className="text-muted-foreground">
                Le prossime sessioni verranno pubblicate a breve.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Conferma Iscrizione</DialogTitle>
            <DialogDescription>
              Stai per iscriverti alla sessione "{selectedSession?.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-secondary/50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-3">
                {selectedSession?.docente_photo ? (
                  <img 
                    src={getFullUrl(selectedSession.docente_photo)} 
                    alt={selectedSession.docente_name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{selectedSession?.docente_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedSession?.docente_specialization}</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>📅 {selectedSession && formatDate(selectedSession.date)}</p>
                <p>🕐 {selectedSession?.time} ({selectedSession?.duration_hours}h)</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-800">
                📧 Riceverai un'email di promemoria <strong>3 giorni prima</strong> della sessione.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={setPrivacyAccepted}
                data-testid="privacy-checkbox"
              />
              <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                Accetto l'informativa sulla privacy e autorizzo il trattamento dei miei dati personali.
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setBookingDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button
              className="rounded-full btn-shine"
              onClick={handleBooking}
              disabled={booking || !privacyAccepted}
              data-testid="confirm-booking-btn"
            >
              {booking ? "Iscrizione..." : "Conferma Iscrizione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{selectedSession?.title}</DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Docente */}
            <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-xl">
              {selectedSession?.docente_photo ? (
                <img 
                  src={selectedSession.docente_photo} 
                  alt={selectedSession.docente_name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
              )}
              <div>
                <p className="font-medium text-lg">{selectedSession?.docente_name}</p>
                {selectedSession?.docente_specialization && (
                  <p className="text-muted-foreground">{selectedSession.docente_specialization}</p>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Data</p>
                <p className="font-medium">{selectedSession && formatDate(selectedSession.date)}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Orario</p>
                <p className="font-medium">{selectedSession?.time} ({selectedSession?.duration_hours}h)</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Posti</p>
                <p className="font-medium">
                  {selectedSession?.current_participants}/{selectedSession?.max_participants}
                </p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Minimo Partecipanti</p>
                <p className="font-medium">{selectedSession?.min_participants}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h4 className="font-medium mb-2">Descrizione</h4>
              <p className="text-muted-foreground">{selectedSession?.description}</p>
            </div>

            {/* Video */}
            {selectedSession?.video_url && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  Video di Esempio
                </h4>
                <video
                  src={selectedSession.video_url}
                  controls
                  className="w-full rounded-xl bg-black"
                />
              </div>
            )}

            {/* Materials */}
            {selectedSession?.materials?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Materiali Didattici
                </h4>
                <div className="space-y-2">
                  {selectedSession.materials.map((material) => (
                    <a
                      key={material.material_id}
                      href={material.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl hover:bg-secondary transition-colors"
                    >
                      <FileText className="w-5 h-5 text-primary" />
                      <span className="flex-1">{material.name}</span>
                      <Download className="w-4 h-4 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedSession?.notes && (
              <div className="p-4 bg-amber-50 rounded-xl">
                <p className="text-sm text-amber-800">
                  📝 <strong>Note:</strong> {selectedSession.notes}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setDetailDialogOpen(false)}
            >
              Chiudi
            </Button>
            {selectedSession && !isAlreadyBooked(selectedSession.session_id) && !isFull(selectedSession) && (
              <Button
                className="rounded-full btn-shine"
                onClick={() => {
                  setDetailDialogOpen(false);
                  openBookingDialog(selectedSession);
                }}
              >
                Iscriviti
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOLEA Chat */}
      <SoleaChat 
        isOpen={soleaChatOpen} 
        onClose={() => setSoleaChatOpen(false)} 
        autoOpened={videoWatched}
      />
    </PageLayout>
  );
};

export default SessionsPage;
